/* global Vue, LOCALE, useOffers, AuthService, PROPERTY_LIST_URL, AlgoliaEvents, ALGOLIA_APP_ID, ALGOLIA_PUBLIC_API_KEY, ALGOLIA_INDEX */

import { messagesSpecific } from '../i18n/translations'
import { CreateI18n } from '../i18n/create-i18n'
import OfferList from './components/OfferList'
import OfferFilters from './components/OfferFilters'
import OfferSlider from './components/OfferSlider'
import LoyaltyBanner from '../loyalty/components/LoyaltyBanner'
import { hideSkeleton, setupScrollRestoration, initScrollRestoration } from '../utils-common'

const i18n = CreateI18n(messagesSpecific)

const OfferListApp = Vue.createApp({
  name: 'OfferListingApp',
  components: { OfferFilters, OfferList, OfferSlider, LoyaltyBanner },
  template: `
  <div class="special-offers">
    <div class="container container--fluid section-margin-top section-margin-small-bottom">
      <h2>{{ $t('offers.recommended') }}</h2>
    </div>
    <div class="recommended-offers">
      <OfferSlider :offers="recommendedOffers" variant="full-width" />
    </div>
    <div class="container container--fluid section-margin-top">
      <h2>{{ $t('offers.popularNow') }}</h2>
    </div>
    <OfferSlider :offers="popularOffers" />
    <div class="container container--fluid">
      <h2>{{ $t('offers.allOffers') }}</h2>
    </div>
    <OfferFilters
      :isFluid="true"
      :values="filters"
      :availableHolidayTypes="availableHolidayTypes"
      :availableBrands="availableBrands"
      :availableDestinations="availableDestinations"
      :properties="properties"
      :availableProperties="availableProperties"
      @update="filters = $event"
      @clear="filters = { ...defaultFilters }" />
    <div class="container" v-if="offersLoaded && offers.length">
      <p class="uppercase">{{ filteredOffers.length.toString() }} {{ $t('offers.nrOfAvailable') }}</p>
    </div>
    <div class="container container--fluid no-padding-xs-down">
      <OfferList :offers="filteredOffers" />
    </div>
    <div class="container" v-if="offersLoaded && !offers.length">
      <h3>{{ $t('offers.titleNoAvailable') }}</h3>
    </div>
  </div>

  <div class="container grid align-items-center">
    <div class="col-12 col-lg-10 offset-lg-1">
      <LoyaltyBanner v-if="!isMember" class="p-margin-top" />
    </div>
  </div>
  `,
  setup () {
    setupScrollRestoration()

    const offers = Vue.ref([])
    const properties = Vue.ref([])
    const offersLoaded = Vue.ref(false)
    const recommendedOffersRaw = Vue.ref([])

    const defaultFilters = {
      holidayType: '',
      brand: '',
      destination: '',
      properties: '',
    }

    const initialFilters = Object.fromEntries(new URLSearchParams(window.location.search).entries())
    if (initialFilters.properties) {
      initialFilters.properties = initialFilters.properties.split(',')
    }
    const filters = Vue.ref({ ...defaultFilters, ...initialFilters })

    Vue.watch(filters, (newFilters) => {
      const queryParams = new URLSearchParams(window.location.search)
      Object.entries(newFilters).forEach(([key, value]) => {
        if (value) {
          queryParams.set(key, value)
        } else {
          queryParams.delete(key)
        }
      })
      history.replaceState({}, '', `?${queryParams.toString()}`)
    }, { deep: true })

    function filterByHolidayType (offer) {
      if (!filters.value.holidayType) {
        return true
      }
      if (!offer.holidayTypes) {
        return false
      }
      return offer.holidayTypes.includes(filters.value.holidayType)
    }

    function filterByBrand (offer) {
      if (!filters.value.brand) {
        return true
      }
      return offer.properties.some(propertySlug => {
        const property = properties.value.find(p => `${p.propertyGroup}${p.campingType ? '-' + p.campingType : ''}` === propertySlug)
        return property && property.brand === filters.value.brand
      })
    }

    function filterByDestination (offer) {
      if (!filters.value.destination) {
        return true
      }
      return offer.destinations.includes(filters.value.destination)
    }

    function filterByProperty (offer) {
      if (!filters.value.properties) {
        return true
      }

      return offer.properties.some(propertySlug => {
        return filters.value.properties.includes(propertySlug)
      })
    }

    const availableHolidayTypes = Vue.computed(() => {
      return offers.value
        .filter(filterByBrand)
        .filter(filterByDestination)
        .filter(filterByProperty)
        .reduce((acc, offer) => {
          if (offer.holidayTypes) {
            offer.holidayTypes.forEach(holidayType => {
              if (!acc.includes(holidayType)) {
                acc.push(holidayType)
              }
            })
          }
          return acc
        }, [])
    })

    const availableBrands = Vue.computed(() => {
      return offers.value
        .filter(filterByHolidayType)
        .filter(filterByDestination)
        .filter(filterByProperty)
        .reduce((acc, offer) => {
          offer.properties.forEach(propertySlug => {
            const property = properties.value.find(p => `${p.propertyGroup}${p.campingType ? '-' + p.campingType : ''}` === propertySlug)
            if (property && property.brand && !acc.includes(property.brand)) {
              acc.push(property.brand)
            }
          })

          if (offer.brand && !acc.includes(offer.brand)) {
            acc.push(offer.brand)
          }
          return acc
        }, [])
    })

    const availableDestinations = Vue.computed(() => {
      return offers.value
        .filter(filterByHolidayType)
        .filter(filterByBrand)
        .filter(filterByProperty)
        .reduce((acc, offer) => {
          if (offer.destinations) {
            offer.destinations.forEach(destination => {
              if (!acc.includes(destination)) {
                acc.push(destination)
              }
            })
          }
          return acc
        }, [])
    })

    const availableProperties = Vue.computed(() => {
      return offers.value
        .filter(filterByHolidayType)
        .filter(filterByBrand)
        .filter(filterByDestination)
        .reduce((acc, offer) => {
          offer.properties.forEach(property => {
            if (!acc.includes(property)) {
              acc.push(property)
            }
          })
          return acc
        }, [])
    })

    const filteredOffers = Vue.computed(() => {
      return offers.value
        .filter(filterByHolidayType)
        .filter(filterByBrand)
        .filter(filterByDestination)
        .filter(filterByProperty)
    })

    const recommendedOffers = Vue.computed(() => {
      const r = recommendedOffersRaw.value
        .slice(0, 3)
        .map(hit => offers.value.find(o => o.link === `${hit.objectID}/`))
        .filter(o => o)
      return r
    })

    const popularOffers = Vue.computed(() => {
      const r = offers.value
        .filter(o => o.popularity)
        .sort((a, b) => a.popularity - b.popularity)
        .slice(0, 3)
      return r
    })

    async function getOffers () {
      offers.value = await useOffers({ isList: true })
      offersLoaded.value = true
      // offers.value = offers.value.splice(0, 4)
      hideSkeleton()
    }

    async function getProperties () {
      const response = await fetch(PROPERTY_LIST_URL)
      properties.value = await response.json()
    }

    const { liteClient: algoliasearch } = window['algoliasearch/lite']
    const searchClient = algoliasearch(
      ALGOLIA_APP_ID,
      ALGOLIA_PUBLIC_API_KEY,
    )

    async function getRecommendedOffers () {
      const userToken = await AlgoliaEvents.getUserTokenAsync()
      return searchClient
        .search([{ indexName: ALGOLIA_INDEX, userToken, facetFilters: [[`lang:${LOCALE === 'si' ? 'sl' : LOCALE}`], ['searchable:true'], ['type:offer']], hitsPerPage: 100 }])
        .then(({ results }) => {
          recommendedOffersRaw.value = results[0].hits
        })
    }

    Vue.onMounted(async () => {
      // Start all async operations
      const offersPromise = getOffers()
      const propertiesPromise = getProperties()
      let recommendedPromise = Promise.resolve()

      if (AuthService.isFinished) {
        recommendedPromise = getRecommendedOffers()
      }

      AuthService.on('ended', async () => {
        recommendedPromise = getRecommendedOffers()
      })

      // Wait for all content to load before restoring scroll
      await Promise.all([offersPromise, propertiesPromise, recommendedPromise])

      Vue.nextTick(() => {
        initScrollRestoration()
      })
    })

    return {
      offers,
      offersLoaded,
      recommendedOffers,
      popularOffers,
      defaultFilters,
      filteredOffers,
      filters,
      availableHolidayTypes,
      availableBrands,
      availableDestinations,
      properties,
      availableProperties,
    }
  },
})

const appElement = document.getElementById('offer-list-app')

if (appElement) {
  OfferListApp.use(i18n)
  OfferListApp.mount(appElement)
}
